import { useState } from 'react';
import type { ChangeEvent } from 'react';
import { Check } from 'lucide-react';
import { Field, KioskField, getFieldTypeSpec } from '@cloudsign/shared';
import type { PreArrivalView, SignatureStrokes } from '@cloudsign/shared';
import { t } from '../../lib/i18n';
import { Alert, Button, CheckboxField, TextField } from '../../components/ui';
import { FieldInput, checkAnswer, rawFromValue } from '../FieldInput';
import type { Raw } from '../FieldInput';
import { NoticeView } from '../fields-notices/noticeText';
import { StrokePad } from './StrokePad';
import { makePhotoPair } from './photo';
import { Section } from './PublicShell';

/** The server sends the group's sign-in questions as field shapes (full or the kiosk's slim form); anything unreadable is skipped. */
export function toField(q: unknown): Field | null {
  const full = Field.safeParse(q);
  if (full.success) return full.data;
  const kiosk = KioskField.safeParse(q);
  if (!kiosk.success) return null;
  const now = new Date().toISOString();
  return { ...kiosk.data, siteId: null, groupId: null, groupIds: [], starred: false, status: 'active', createdAt: now, updatedAt: now };
}

const widgetOf = (f: Field) => getFieldTypeSpec(f.typeKey)?.widget ?? 'text';
/** Host search needs a portal session and a signature is drawn at the screen, so neither is asked on the public page. */
const askable = (f: Field) => widgetOf(f) !== 'member_picker' && widgetOf(f) !== 'signature';

export function Questions({ view, pending, error, onSave }: { view: PreArrivalView; pending: boolean; error: string | null; onSave: (a: { fieldId: string; value: unknown }[]) => void }) {
  const fields = view.questions.map(toField).filter((f): f is Field => f !== null && askable(f));
  const [raw, setRaw] = useState<Record<string, Raw>>(() => Object.fromEntries(fields.map((f) => [f.id, rawFromValue(f, view.answers.find((a) => a.fieldId === f.id)?.value)])));
  const [errors, setErrors] = useState<Record<string, string>>({});
  if (fields.length === 0) return null;

  const save = () => {
    const bad: Record<string, string> = {};
    const answers: { fieldId: string; value: unknown }[] = [];
    for (const f of fields) {
      const r = checkAnswer(f, raw[f.id]);
      if (!r.ok) bad[f.id] = r.messageKey;
      else if (r.value !== null && r.value !== undefined) answers.push({ fieldId: f.id, value: r.value });
    }
    setErrors(bad);
    if (Object.keys(bad).length === 0) onSave(answers);
  };

  return (
    <Section title={t('publicLinks.pre.questions.title')}>
      {fields.map((f) => (
        <FieldInput key={f.id} field={f} raw={raw[f.id]} error={errors[f.id]} onChange={(v) => setRaw((p) => ({ ...p, [f.id]: v }))} />
      ))}
      {error ? <Alert>{error}</Alert> : null}
      <Button variant="primary" className="w-full" disabled={pending} onClick={save}>
        {t('publicLinks.pre.questions.save')}
      </Button>
    </Section>
  );
}

function NoticeItem({ notice, pending, onAccept }: { notice: PreArrivalView['notices'][number]; pending: boolean; onAccept: (versionId: string, strokes: SignatureStrokes | null) => void }) {
  const [agree, setAgree] = useState(false);
  const [strokes, setStrokes] = useState<SignatureStrokes | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const accept = () => {
    if (!agree) return setProblem(t('publicLinks.pre.notices.needAgree'));
    if (notice.needsSignature && !strokes) return setProblem(t('publicLinks.pre.notices.needSign'));
    setProblem(null);
    onAccept(notice.versionId, notice.needsSignature ? strokes : null);
  };
  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-3">
      <h3 className="font-medium">{notice.title}</h3>
      <div className="max-h-64 overflow-y-auto rounded bg-slate-50 p-3">
        <NoticeView body={notice.body} />
      </div>
      {notice.accepted ? (
        <p className="flex items-center gap-2 text-sm font-medium text-green-800">
          <Check aria-hidden="true" className="h-4 w-4" />
          {t('publicLinks.pre.notices.accepted')}
        </p>
      ) : (
        <>
          <CheckboxField label={t('publicLinks.pre.notices.agree')} checked={agree} onChange={(e) => setAgree(e.target.checked)} />
          {notice.needsSignature ? (
            <div className="space-y-1">
              <p className="text-sm font-medium">{t('publicLinks.pre.notices.signHere')}</p>
              <StrokePad onChange={setStrokes} />
            </div>
          ) : null}
          {problem ? <p className="text-sm text-red-700">{problem}</p> : null}
          <Button variant="primary" disabled={pending} onClick={accept}>
            {t('publicLinks.pre.notices.accept')}
          </Button>
        </>
      )}
    </div>
  );
}

export function Notices({ view, pending, error, onAccept }: { view: PreArrivalView; pending: boolean; error: string | null; onAccept: (versionId: string, strokes: SignatureStrokes | null) => void }) {
  if (view.notices.length === 0) return null;
  return (
    <Section title={t('publicLinks.pre.notices.title')}>
      <p className="text-sm text-slate-700">{t('publicLinks.pre.notices.help')}</p>
      {view.notices.map((n) => (
        <NoticeItem key={n.id} notice={n} pending={pending} onAccept={onAccept} />
      ))}
      {error ? <Alert>{error}</Alert> : null}
    </Section>
  );
}

export function PhotoCard({ view, pending, error, onUpload }: { view: PreArrivalView; pending: boolean; error: string | null; onUpload: (photo: Blob, thumb: Blob) => void }) {
  const [problem, setProblem] = useState<string | null>(null);
  if (!view.photo.wanted) return null;
  const choose = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return setProblem(t('publicLinks.pre.photo.notImage'));
    const pair = await makePhotoPair(file);
    if (!pair) return setProblem(t('publicLinks.pre.photo.failed'));
    setProblem(null);
    onUpload(pair.photo, pair.thumb);
  };
  return (
    <Section title={t('publicLinks.pre.photo.title')}>
      <p className="text-sm text-slate-700">{t('publicLinks.pre.photo.help')}</p>
      {view.photo.done ? (
        <p className="flex items-center gap-2 text-sm font-medium text-green-800">
          <Check aria-hidden="true" className="h-4 w-4" />
          {t('publicLinks.pre.photo.done')}
        </p>
      ) : null}
      <label className="inline-flex min-h-12 w-full cursor-pointer items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2 text-base font-medium has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-brand">
        {view.photo.done ? t('publicLinks.pre.photo.change') : t('publicLinks.pre.photo.choose')}
        <input type="file" accept="image/*" capture="user" className="sr-only" disabled={pending} onChange={(e) => void choose(e)} />
      </label>
      {problem || error ? <Alert>{problem ?? error}</Alert> : null}
    </Section>
  );
}

export function CarCard({ view, pending, error, onSave }: { view: PreArrivalView; pending: boolean; error: string | null; onSave: (registration: string | null) => void }) {
  const [value, setValue] = useState(view.carRegistration.value ?? '');
  if (!view.carRegistration.wanted) return null;
  return (
    <Section title={t('publicLinks.pre.car.title')}>
      <TextField label={t('publicLinks.pre.car.label')} hint={t('publicLinks.pre.car.help')} value={value} maxLength={15} autoCapitalize="characters" autoComplete="off" onChange={(e) => setValue(e.target.value.toUpperCase())} />
      {error ? <Alert>{error}</Alert> : null}
      <Button variant="primary" className="w-full" disabled={pending} onClick={() => onSave(value.trim() === '' ? null : value.trim())}>
        {t('publicLinks.pre.car.save')}
      </Button>
    </Section>
  );
}
